import { useMemo } from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { AlertStatus } from "@/alerts/types/machine-alert";
import { DEFAULT_ALARM_SOUND_ID, getAlarmSound } from "@/alerts/utils/alarm-sounds";
import { safeStorage } from "@/shared/lib/storage/safe-storage";

/** A live alert surfaced in the header bell (resolved for display). */
export interface AlertNotification {
  id: number;
  type: string;
  status: AlertStatus;
  machineName: string;
  deviceMxid: string;
  /** ISO timestamp. */
  timestamp: string;
}

const MAX_ITEMS = 50;
/** Cap the dedup history so persisted localStorage can't grow without bound. */
const MAX_KNOWN = 5000;
/**
 * Only alerts younger than this may be flagged "new" (chime + highlight).
 * Older alerts entering the feed (widened time window, backfill, cleared
 * cache) merge silently as backlog, and an existing highlight ages out once
 * the alert falls outside the window.
 */
const NEW_ALERT_MAX_AGE_MS = 30 * 60 * 1000;

interface AlertNotifyState {
  /** Live notifications, newest first (bell popover). */
  items: AlertNotification[];
  /** Ids the operator has already seen — drives the bell's unseen badge. */
  seenIds: number[];
  /**
   * Ids of freshly-arrived alerts that are still unresolved — drives the map +
   * list "new" highlight. Decoupled from `seenIds`: viewing the bell clears the
   * badge but NOT the highlight, which clears when the alert is resolved
   * (status leaves Pending) or ages past {@link NEW_ALERT_MAX_AGE_MS}.
   * Persisted, so it survives a refresh.
   */
  newIds: number[];
  /** Current data scope (e.g. location id). Changing it re-seeds silently. */
  scope: string | null;
  /**
   * Every alert id ever seen this scope — for dedup. (`items` is capped for
   * display, so it can't be the dedup source or capped-out alerts would look
   * "new" forever.) Capped to {@link MAX_KNOWN}.
   */
  knownIds: number[];
  /** Whether the new-alert chime is muted (persisted). */
  muted: boolean;
  /**
   * Whether the map may open a newly-arrived alert BY ITSELF (persisted).
   *
   * Off is for validating PAST days. With it on, every arrival re-targets the
   * map camera, the active machine, the timeline and the video onto the newest
   * alert, so an operator working backwards through yesterday is repeatedly
   * thrown to the last alert of today and has to find their place again.
   *
   * Deliberately separate from {@link AlertNotifyState.muted}: this stops the
   * operator being MOVED, not being TOLD. The bell, the badge, the map's "new"
   * highlight and the alarm are all untouched — an alert that arrives while
   * following is off is still announced, it just waits to be clicked.
   */
  followNewAlerts: boolean;
  /** Chosen alarm pattern id (persisted) — see utils/alarm-sounds. */
  alarmSoundId: string;
  /**
   * Whether the operator has acknowledged the current new alerts by viewing one
   * on /map — suppresses the repeat-until-acknowledged chime. Reset to false the
   * moment a genuinely-new alert arrives (so it rings again), set true by
   * {@link acknowledgeChime}. Decoupled from the bell badge: opening the bell
   * clears the badge (seenIds) but NOT this, so the chime keeps looping until the
   * alert is actually seen on /map. Persisted so a refresh keeps a prior ack.
   */
  chimeAcknowledged: boolean;
  /**
   * Merge the current alert list in for a given scope. Empty batches (still
   * loading) are ignored. The first batch of a *new* scope seeds everything as
   * the existing backlog — seen, known, not "new" — so switching location
   * doesn't chime or highlight its backlog. Within the same scope, ids we've
   * never seen are added as unseen + new — but only when Pending AND no older
   * than {@link NEW_ALERT_MAX_AGE_MS}; older arrivals merge silently as
   * backlog. Newly-resolved or aged-out ids drop out of the highlight.
   *
   * Fills the BELL only — it never starts a sound. Returns the fresh recent
   * Pending alerts so the caller can put them through the sound gate
   * ({@link AlertNotifyState.armChime}).
   */
  notify: (incoming: AlertNotification[], scope: string) => AlertNotification[];
  /**
   * Arm the alarm for specific alerts.
   *
   * The ONLY thing that starts a sound. `notify` deliberately no longer does it:
   * it cannot, because MIN-223 decides sound from a Firestore hint reconciled
   * against a fresh queue page, and a store that only sees a list of rows has no
   * access to the hint's `signal` or `review_cycle`. Splitting the two means the
   * feed can fill the bell on every poll while sound stays governed by the
   * ticket's six conditions — see `utils/sound-eligibility.ts`.
   *
   * The ids are unioned into `newIds` so the badge and the sound can never
   * describe different sets. A no-op for an empty list, so an ineligible batch
   * cannot re-arm a chime the operator already silenced.
   */
  armChime: (ids: number[]) => void;
  markAllSeen: () => void;
  setAlarmSoundId: (id: string) => void;
  /** Drop an alert from the "new" highlight immediately (e.g. on Resolve/False). */
  markHandled: (id: number) => void;
  /** Acknowledge the current chime (operator viewed an alert on /map). */
  acknowledgeChime: () => void;
  setMuted: (muted: boolean) => void;
  setFollowNewAlerts: (followNewAlerts: boolean) => void;
  /** Wipe all alert data (called on sign-out so it never leaks to the next user). */
  reset: () => void;
}

const sortNewestFirst = (items: AlertNotification[]) =>
  [...items]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, MAX_ITEMS);

const isPending = (a: AlertNotification) => a.status === AlertStatus.Pending;

const isRecent = (a: AlertNotification) =>
  Date.now() - new Date(a.timestamp).getTime() <= NEW_ALERT_MAX_AGE_MS;

/**
 * Scrub persisted new ids on rehydrate. `acknowledged` mirrors the retention
 * rule in `notify`: while the alarm is UNacknowledged, a still-Pending alert
 * survives past the recency window, so a page refresh can't silence an alarm the
 * operator never dealt with. Defaults to true — callers checking pure staleness
 * keep the original behaviour.
 */
export const pruneNewIds = (
  items: AlertNotification[],
  newIds: number[],
  acknowledged = true,
) => {
  const itemById = new Map(items.map((item) => [item.id, item]));

  return newIds.filter((id) => {
    const item = itemById.get(id);
    if (!item) return false;
    return isPending(item) && (isRecent(item) || !acknowledged);
  });
};

export const useAlertNotifyStore = create<AlertNotifyState>()(
  persist(
    (set, get) => ({
      items: [],
      seenIds: [],
      newIds: [],
      scope: null,
      knownIds: [],
      muted: false,
      followNewAlerts: true,
      alarmSoundId: DEFAULT_ALARM_SOUND_ID,
      chimeAcknowledged: false,

      notify: (incoming, scope) => {
        if (incoming.length === 0) return []; // still loading — ignore
        const state = get();
        if (state.scope !== scope) {
          // New scope (location/org switch): the whole list is the backlog.
          const ids = incoming.map((a) => a.id);
          set({
            items: sortNewestFirst(incoming),
            seenIds: ids,
            knownIds: ids.slice(-MAX_KNOWN),
            newIds: [],
            scope,
            // Backlog seed — nothing new to chime for this scope.
            chimeAcknowledged: true,
          });
          return [];
        }

        const known = new Set(state.knownIds);
        const fresh = incoming.filter((a) => !known.has(a.id));
        const byId = new Map(incoming.map((a) => [a.id, a]));
        const itemById = new Map(state.items.map((a) => [a.id, a]));

        // Keep prior new ids that are still Pending; add freshly-arrived Pending
        // ids from the last 30 minutes. Resolved/Ignored ones always fall out.
        //
        // `isRecent` gates ARRIVAL — a backlog alert must never chime. It must
        // NOT also gate retention while the alarm is unacknowledged: an alert
        // that stays Pending because nobody opened it would then drop out of the
        // highlight after 30 minutes and silence its own alarm, which is exactly
        // the alert that most needs to keep ringing. Once the operator
        // acknowledges, ageing resumes clearing the highlight as before.
        const keptNew = state.newIds.filter((id) => {
          const a = byId.get(id) ?? itemById.get(id);
          if (!a) return true;
          return isPending(a) && (isRecent(a) || !state.chimeAcknowledged);
        });
        const freshNew = fresh
          .filter((a) => isPending(a) && isRecent(a))
          .map((a) => a.id);
        const nextNew = Array.from(new Set([...keptNew, ...freshNew]));

        const prevNew = new Set(state.newIds);
        const newChanged =
          nextNew.length !== prevNew.size || nextNew.some((id) => !prevNew.has(id));

        // Nothing arrived and the highlight set is unchanged → no-op (avoids a
        // re-render loop, since the alert list gets a fresh identity each poll).
        if (fresh.length === 0 && !newChanged) return [];

        // Fresh-but-old alerts join the list silently: mark them seen so they
        // don't ring the bell badge either.
        const staleFreshIds = fresh.filter((a) => !isRecent(a)).map((a) => a.id);

        set({
          items: fresh.length ? sortNewestFirst([...fresh, ...state.items]) : state.items,
          knownIds: fresh.length
            ? [...state.knownIds, ...fresh.map((a) => a.id)].slice(-MAX_KNOWN)
            : state.knownIds,
          seenIds: staleFreshIds.length
            ? [...state.seenIds, ...staleFreshIds].slice(-MAX_KNOWN)
            : state.seenIds,
          newIds: nextNew,
          // NOT re-armed here. Filling the bell and starting a klaxon are
          // different decisions with different evidence: this function sees only
          // rows, while MIN-223 gates sound on a hint's `signal` and
          // `review_cycle` reconciled against those rows. `armChime` is the one
          // door in — see its doc comment.
        });
        return fresh.filter((a) => isPending(a) && isRecent(a));
      },

      armChime: (ids) =>
        set((state) => {
          if (ids.length === 0) return state;
          const nextNew = Array.from(new Set([...state.newIds, ...ids]));
          return { newIds: nextNew, chimeAcknowledged: false };
        }),

      markAllSeen: () =>
        set((state) => ({
          seenIds: Array.from(new Set([...state.seenIds, ...state.items.map((a) => a.id)])),
        })),

      markHandled: (id) =>
        set((state) =>
          state.newIds.includes(id)
            ? { newIds: state.newIds.filter((x) => x !== id) }
            : state,
        ),

      acknowledgeChime: () => set({ chimeAcknowledged: true }),

      setMuted: (muted) => set({ muted }),

      setFollowNewAlerts: (followNewAlerts) => set({ followNewAlerts }),

      setAlarmSoundId: (alarmSoundId) => set({ alarmSoundId }),

      // Clears in-memory + (via persist) the stored copy. Keeps the operator's
      // mute and follow preferences.
      reset: () =>
        set({ items: [], seenIds: [], newIds: [], knownIds: [], scope: null, chimeAcknowledged: false }),
    }),
    {
      // v2 (2026-07-24): new key forces a clean slate on deploy — the old
      // persisted `items`/`seenIds` were keeping stale alerts in the bell.
      name: "maestro-alert-notify-v2",
      // Guarded storage so a full localStorage can't crash the app on write.
      storage: createJSONStorage(() => safeStorage),
      // Persist the live list + tracking so a refresh keeps prior alerts and the
      // "new" highlight (alerts seen "from before", not just the latest refresh).
      partialize: (s) => ({
        muted: s.muted,
        followNewAlerts: s.followNewAlerts,
        alarmSoundId: s.alarmSoundId,
        scope: s.scope,
        items: s.items,
        seenIds: s.seenIds,
        knownIds: s.knownIds,
        newIds: s.newIds,
        chimeAcknowledged: s.chimeAcknowledged,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState as Partial<AlertNotifyState> | undefined) ?? {};
        const items = Array.isArray(persisted.items) ? persisted.items : currentState.items;
        const chimeAcknowledged =
          typeof persisted.chimeAcknowledged === "boolean"
            ? persisted.chimeAcknowledged
            : currentState.chimeAcknowledged;

        return {
          ...currentState,
          ...persisted,
          items,
          seenIds: Array.isArray(persisted.seenIds) ? persisted.seenIds : currentState.seenIds,
          knownIds: Array.isArray(persisted.knownIds) ? persisted.knownIds : currentState.knownIds,
          // A refresh must not silence an alarm the operator never acknowledged.
          newIds: pruneNewIds(
            items,
            Array.isArray(persisted.newIds) ? persisted.newIds : [],
            chimeAcknowledged,
          ),
          chimeAcknowledged,
          // A persisted id from a removed pattern must not silence the alarm.
          alarmSoundId: getAlarmSound(persisted.alarmSoundId).id,
        };
      },
    },
  ),
);

/** The notifications not yet marked seen (newest first) — bell badge + list. */
export const useUnseenNotifications = (): AlertNotification[] => {
  const items = useAlertNotifyStore((s) => s.items);
  const seenIds = useAlertNotifyStore((s) => s.seenIds);
  return useMemo(() => {
    const seen = new Set(seenIds);
    return items.filter((a) => !seen.has(a.id));
  }, [items, seenIds]);
};

/** Ids of freshly-arrived, still-unresolved alerts — the map/list "new" highlight. */
export const useNewAlertIds = (): Set<number> => {
  const newIds = useAlertNotifyStore((s) => s.newIds);
  return useMemo(() => new Set(newIds), [newIds]);
};
